import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { EdgePatientEnrollmentApi } from '@trialcard/apigateway.client/edgePatientEnrollmentApi';
import {
    EnrollPatientRequestModel,
    EnterpriseCoordinatorAccountUpsearchRequestModel,
    UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
    EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    EnterpriseCoordinatorSharedAddressUpsearchModel,
    EnterpriseCoordinatorSharedPhoneUpsearchModel,
    GetBestEnrollmentV2RequestModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel,
    UpsearchModelEnterpriseCoordinatorPharmacyServiceModelsPharmacyUpsearchModel,
    EnterpriseCoordinatorPharmacyServiceModelsPharmacyUpsearchModel,
    EnterpriseCoordinatorSharedContactUpsearchModel,
    UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
} from '@trialcard/apigateway.models';
import { Gender } from '@trialcard/enums';

import { Moment } from 'moment';
import { AxiosResponse } from 'axios';
import { EdgeEnrollmentApi } from '@trialcard/apigateway.client';
import { boolean, number, string } from 'yargs';
import SessionModule from './session';
import { OnClear } from '~/services/clearModule';
import { Settings } from '~/services/settings';

import SurveyModule from '~/store/survey';

export type PathValues = 'speciality-pharmacy' | 'hub' | 'patient';

@InjectModule({ stateFactory: true }, module)
@OnClear<EnrollmentModule>(async value => value.clear())
export default class EnrollmentModule extends InjectVuexModule {
    @Resolve
    public patientEnrollmentApi!: EdgePatientEnrollmentApi;

    @Resolve
    public enrollmentApi!: EdgeEnrollmentApi;

    @Resolve
    public settings!: Settings;

    @Resolve
    public survey!: SurveyModule;

    @Resolve
    public session!: SessionModule;

    alternativeEnrollment: boolean | null = null;

    memberNumber: string | null = null;

    expirationDate: Moment | null = null;

    defaultPatientInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: null as Moment | null,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        gender: null as Gender | null,
    };

    defaultPharmacyInformationData = {
        pharmacyName: '',
        accountRelationshipIsPrimary: true,
        pharmacyId: 1,
        nabp: '',
        pharmacyType: 0, // Other
        pharmacySpecialization: 0, // Other
        contacts: [
            {
                addresses: [
                    {
                        city: '',
                        state: '',
                    },
                ],
                firstName: '',
                lastName: '',
                contactType: 2, // Prescriber
            },
        ],
    };

    defaultContactData = {
        id: 0,
        roleType: 18,
        firstName: '',
        lastName: '',
        phoneNumber: '',
        altPhoneNumber: '',
    };

    defaultSiteData = [
        {
            model: {
                accountRelationshipIsPrimary: true,
                pointOfContacts: [],
                description: '',
                addresses: [
                    {
                        addressOne: '',
                        addressTwo: '',
                        zip: '',
                        city: '',
                        state: '',
                    },
                ],
                contacts: [],
                emailAddresses: [],
                externalIds: [],
                phoneNumbers: [],
                globalIds: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[];

    defaultPrescriberData = [
        {
            model: {
                firstName: '',
                lastName: '',
                npi: '',
                prescriberLicenses: [],
                externalIds: [],
                addresses: [],
                phoneNumbers: [],
                emailAddresses: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[];

    patientInformationData = { ...this.defaultPatientInformationData };
    pharmacyInformationData = { ...this.defaultPharmacyInformationData };
    prescriberInformationData = [...this.defaultPrescriberData];
    siteInformationData = [...this.defaultSiteData];

    contactData = { ...this.defaultContactData };
    contactPreferences = [] as EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[];

    @Mutation
    public setEnrollmentType(alternative: boolean) {
        this.alternativeEnrollment = alternative;
    }

    @Mutation
    public async updatePatientInformation(newPatientInformation: typeof EnrollmentModule.prototype.patientInformationData) {
        this.patientInformationData = { ...this.patientInformationData, ...newPatientInformation };
    }

    @Mutation
    public async updatePharmacyInformation(newPharmacyInformation: typeof EnrollmentModule.prototype.pharmacyInformationData) {
        this.pharmacyInformationData = { ...this.pharmacyInformationData, ...newPharmacyInformation };
    }

    @Mutation
    public async updateSiteInformation(newSiteInformation: UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[]) {
        this.siteInformationData = [...newSiteInformation];
    }

    @Mutation
    public async updatePrescibersInformation(
        newPrescriberData: UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[]
    ) {
        this.prescriberInformationData = [...newPrescriberData];
    }

    @Mutation
    public async updateContactInformation(newContactInformation: typeof EnrollmentModule.prototype.contactData) {
        this.contactData = { ...this.contactData, ...newContactInformation };
    }

    @Mutation
    public clear() {}

    @Mutation
    public setMemberNumber(memberNumber: string | null) {
        this.memberNumber = memberNumber;
    }

    @Mutation
    public setExpirationDate(expirationDate: Moment) {
        this.expirationDate = expirationDate;
    }

    @Action({ rawError: true })
    public clearEnrollment() {
        this.updatePatientInformation({ ...this.defaultPatientInformationData });
        this.setMemberNumber(null);
    }

    @Action({ rawError: true })
    public async checkForExistingEnrollments() {
        const patient: GetBestEnrollmentV2RequestModel = {
            firstName: this.patientInformationData.firstName as string,
            lastName: this.patientInformationData.lastName as string,
            dateOfBirth: this.patientInformationData.dateOfBirth as Moment,
            zip: this.patientInformationData.addresses[0].zip as string,
            gender: this.patientInformationData.gender as Gender,
        };
        const response = await this.enrollmentApi.enrollmentGetBestEnrollmentsV2(patient, this.settings.programId);
        return (response && response.data && response.data.data && response.data.data) || [];
    }

    @Action({ rawError: true })
    public async submitToAPI() {
        let response = null;
        try {
            const patient = {
                account: {
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            ...this.patientInformationData,
                            middleName: '',
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    prescriberData: this.prescriberInformationData,
                    siteData: this.siteInformationData,
                    pharmacyData: [
                        {
                            canUpdate: true,
                            canInsert: true,
                            skipSearch: false,
                            model: {
                                npi: '0',
                                ...this.pharmacyInformationData,
                            } as EnterpriseCoordinatorPharmacyServiceModelsPharmacyUpsearchModel,
                        },
                    ] as UpsearchModelEnterpriseCoordinatorPharmacyServiceModelsPharmacyUpsearchModel[],
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            accountType: 1,
                            accountStatus: 1,
                            contacts: [
                                {
                                    ...this.contactData,
                                } as EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel,
                            ],
                            contactPreferences: this.contactPreferences,
                        },
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    surveySessionData: [this.survey.sessionId] as number[],
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
            } as EnrollPatientRequestModel;

            response = await this.patientEnrollmentApi.patientEnrollmentEnrollPatient(patient, this.settings.programId);
        } catch (error) {
            response = { status: 400 } as AxiosResponse;
        }
        return response;
    }
}
